import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../api';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Mail, Lock, ShieldCheck, FileText, BarChart2, Users, ClipboardList, AlertCircle } from 'lucide-react';
import { BrandMark } from '../components/Layout';

const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animFrame;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 120 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.8 + 0.3,
      dx: (Math.random() - 0.5) * 0.35,
      dy: (Math.random() - 0.5) * 0.35,
      alpha: Math.random() * 0.6 + 0.2,
      color: Math.random() > 0.6 ? '0,212,203' : '37,99,235',
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},${p.alpha})`;
        ctx.fill();
      });
      animFrame = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animFrame); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />;
};

const features = [
  { icon: <FileText size={13} />, label: 'Gestão de Documentos' },
  { icon: <ClipboardList size={13} />, label: 'Auditorias' },
  { icon: <BarChart2 size={13} />, label: 'Indicadores' },
  { icon: <Users size={13} />, label: 'Fornecedores' },
  { icon: <AlertCircle size={13} />, label: 'Não Conformidades' },
];

const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Abaixo de 768px (celulares e tablets em pé) o layout de 2 colunas não
  // cabe — o painel do formulário (420px fixos) ficava cortado para fora
  // da tela. Nesse caso empilha-se: uma faixa de marca compacta em cima e
  // o formulário abaixo, ocupando a largura toda.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Preencha todos os campos'); return; }
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) { toast.success('Login realizado com sucesso!'); }
    else if (String(result.error).includes('Acesso não permitido a partir desta rede')) {
      // Bloqueio por IP: destaque em azul para diferenciar de erro de senha
      toast.error(result.error, {
        style: { background: '#173efc', border: '1px solid #173efc', color: '#fff', fontWeight: 600 },
      });
    }
    else { toast.error(result.error); }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #060b17 0%, #0b1425 50%, #07111e 100%)',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      position: 'relative',
      overflow: isMobile ? 'auto' : 'hidden',
    }}>
      <ParticleCanvas />

      {/* LEFT — Branding (compactada e sem os detalhes decorativos no celular) */}
      <div style={{
        flex: isMobile ? 'none' : 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isMobile ? 'flex-start' : 'space-between',
        padding: isMobile ? '2rem 1.5rem 1rem' : '2.5rem 3rem',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start' }}>
          <BrandMark size={isMobile ? 56 : 72} textSize={isMobile ? 26 : 34} />
        </div>

        {/* Hero content */}
        <div style={{ maxWidth: '520px', margin: isMobile ? '1.5rem auto 0' : 0, textAlign: isMobile ? 'center' : 'left' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.35)',
            borderRadius: '20px', padding: '0.3rem 0.9rem', marginBottom: '1.6rem',
          }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#00D4CB' }} />
            <span style={{ color: '#00D4CB', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em' }}>
              SISTEMA DE GESTÃO DA QUALIDADE
            </span>
          </div>

          <h1 style={{ fontSize: isMobile ? '1.9rem' : '2.8rem', fontWeight: 800, lineHeight: 1.15, color: '#ffffff', marginBottom: '0.3rem' }}>
            Qualidade que
          </h1>
          <h1 style={{ fontSize: isMobile ? '1.9rem' : '2.8rem', fontWeight: 800, lineHeight: 1.15, color: '#00D4CB', marginBottom: '1.4rem' }}>
            gera resultados.
          </h1>

          {/* Parágrafo e chips de recursos só cabem bem em telas maiores;
              no celular eles só empurrariam o formulário para baixo. */}
          {!isMobile && (
            <>
              <p style={{ color: '#94a3b8', fontSize: '1rem', lineHeight: 1.7, marginBottom: '2rem', maxWidth: '420px' }}>
                SGQ completo com gestão de documentos, auditorias, indicadores de desempenho e inteligência para acelerar sua excelência operacional.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                {features.map((f, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px', padding: '0.38rem 0.85rem',
                    color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 500,
                  }}>
                    <span style={{ color: '#00D4CB' }}>{f.icon}</span> {f.label}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer — escondido no celular pra não empurrar o formulário mais para baixo */}
        {!isMobile && (
          <p style={{ color: '#334155', fontSize: '0.78rem' }}>
            © 2025 QualySynX · SGQ v1.0.0 · Todos os direitos reservados
          </p>
        )}
      </div>

      {/* RIGHT — Form */}
      <div style={{
        width: isMobile ? '100%' : '420px',
        flexShrink: 0,
        boxSizing: 'border-box',
        background: 'rgba(10,18,35,0.85)',
        backdropFilter: 'blur(20px)',
        borderLeft: isMobile ? 'none' : '1px solid rgba(37,99,235,0.12)',
        borderTop: isMobile ? '1px solid rgba(37,99,235,0.12)' : 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? '2rem 1.5rem 2.5rem' : '2.5rem 2.5rem',
        position: 'relative', zIndex: 1,
      }}>
        <div style={{ width: '100%' }}>
          <h2 style={{ color: '#ffffff', fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.4rem' }}>
            Bem-vindo 👋
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '2rem' }}>
            Acesse sua conta para continuar
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
            {/* Email */}
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                EMAIL CORPORATIVO
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  disabled={loading}
                  data-testid="login-email"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '10px', padding: '0.78rem 1rem 0.78rem 2.8rem',
                    color: '#e2e8f0', fontSize: '0.92rem', outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = '#2563EB'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
                />
                <Mail size={15} style={{ position: 'absolute', left: '0.9rem', top: '50%', transform: 'translateY(-50%)', color: '#475569' }} />
              </div>
            </div>

            {/* Password */}
            <div>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                SENHA
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={loading}
                  data-testid="login-password"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '10px', padding: '0.78rem 2.8rem 0.78rem 2.8rem',
                    color: '#e2e8f0', fontSize: '0.92rem', outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = '#2563EB'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
                />
                <Lock size={15} style={{ position: 'absolute', left: '0.9rem', top: '50%', transform: 'translateY(-50%)', color: '#475569' }} />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  style={{ position: 'absolute', right: '0.9rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 0, display: 'flex' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              style={{
                width: '100%', padding: '0.82rem',
                background: 'linear-gradient(135deg, #2563EB 0%, #00D4CB 100%)',
                border: 'none', borderRadius: '10px',
                color: '#ffffff', fontSize: '0.95rem', fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                transition: 'opacity 0.2s, transform 0.2s',
                marginTop: '0.3rem',
              }}
              onMouseEnter={e => { if (!loading) e.target.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; }}
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Entrando...</> : <><ShieldCheck size={16} /> Entrar no Sistema</>}
            </button>
          </form>

          <div style={{ marginTop: '1.8rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
            <p style={{ color: '#334155', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <ShieldCheck size={13} style={{ color: '#22C55E' }} /> Conexão segura e criptografada
            </p>
            <p style={{ color: '#334155', fontSize: '0.78rem' }}>
              Problemas de acesso? Contate o administrador
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;